import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useLandingVariant } from '../../hooks/useLandingVariant';
import { checkBackendHealth } from '../../api/health';
import { isStandaloneMode } from '../../lib/service-worker';
import { LandingPageA } from './LandingPageA';
import { LandingPageB } from './LandingPageB';
import { LandingPageC } from './LandingPageC';

/**
 * Routes to the correct landing page variant based on:
 * 1. URL param ?v=a|b|c (from ads / referral links)
 * 2. localStorage (returning visitor)
 * 3. Random assignment (new organic visitor)
 *
 * Ad routing examples:
 *   Product Hunt / HN → askdanny.to/?v=a  (The Receipt)
 *   Reddit ads         → askdanny.to/?v=b  (The Upgrade)
 *   Referral links     → askdanny.to/?v=c  (The Demo)
 *
 * Exception: when the user is in standalone (installed PWA) mode, we never
 * want them to see the marketing pages — they tapped the app icon expecting
 * the app. The site.webmanifest's `start_url` was updated to `/app` for new
 * installs, but anyone whose PWA was installed before that change still has
 * `/` baked in. This runtime check covers them — preserves any query params
 * (share_target intents, ?view=today shortcuts) when redirecting.
 */
export function LandingRouter() {
  const variant = useLandingVariant();

  // Warm up the API backend on page load (Cloud Run cold start mitigation).
  // Fire-and-forget — result is ignored, we just want to wake the instance.
  useEffect(() => { checkBackendHealth(); }, []);

  if (isStandaloneMode()) {
    const search = window.location.search;
    return <Navigate to={`/app${search}`} replace />;
  }

  switch (variant) {
    case 'a':
      return <LandingPageA />;
    case 'b':
      return <LandingPageB />;
    case 'c':
      return <LandingPageC />;
  }
}
